export const CAPTAIN_WORKFLOW_STAGE = Object.freeze({
    TEAM_NAMING: 'team_naming',
    CAPTAIN_ASSIGNMENT: 'captain_assignment',
    GREEN_ADJUSTMENT: 'green_adjustment',
    RED_SELECTION: 'red_selection',
    LINEUP_SETUP: 'lineup_setup',
    READY_TO_START: 'ready_to_start'
})

export const CAPTAIN_WORKFLOW_STEPS = Object.freeze([
    { title: '管理员', description: '平衡位置', icon: '👤' },
    { title: '主队队长', description: '调换队员', icon: '🏠' },
    { title: '客队队长', description: '选队', icon: '✈️' },
    { title: '双方队长', description: '排首发', icon: '⚽' }
])

export const INTERNAL_CAPTAIN_WORKFLOW_STEPS = Object.freeze([
    { title: '主队队长', description: '命名两队', icon: '✍️' },
    { title: '主队队长', description: '调整成员', icon: '🏠' },
    { title: '客队队长', description: '选择队伍', icon: '✈️' },
    { title: '双方队长', description: '安排阵容', icon: '⚽' }
])

export const LINEUP_VIEW_MODE = Object.freeze({
    CHOOSE_FORMATION: 'choose_formation',
    EDIT_LINEUP: 'edit_lineup',
    READ_ONLY: 'read_only'
})

const normalizeConfirm = (value) => value == null ? null : Number(value)

// 将后端的两个确认数字转换成唯一的页面阶段，组件不再自行拼接状态条件。
export function getCaptainWorkflow(gameDetail = {}) {
    const greenConfirm = normalizeConfirm(gameDetail.greenCaptainConfirm)
    const redConfirm = normalizeConfirm(gameDetail.redCaptainConfirm)
    const isInternalMatch = Number(gameDetail.gameType) === 3
    const hasTeamNames = Boolean(String(gameDetail.greenName || '').trim() && String(gameDetail.redName || '').trim())
    const steps = isInternalMatch ? INTERNAL_CAPTAIN_WORKFLOW_STEPS : CAPTAIN_WORKFLOW_STEPS
    let stage = CAPTAIN_WORKFLOW_STAGE.CAPTAIN_ASSIGNMENT
    let stepIndex = 0

    // 内讧赛只有四步；已命名但确认值仍为空的旧数据直接进入主队调人。
    if (isInternalMatch && !hasTeamNames && greenConfirm === null && redConfirm === null) {
        stage = CAPTAIN_WORKFLOW_STAGE.TEAM_NAMING
        stepIndex = 0
    } else if (isInternalMatch && hasTeamNames && greenConfirm === null && redConfirm === null) {
        stage = CAPTAIN_WORKFLOW_STAGE.GREEN_ADJUSTMENT
        stepIndex = 1
    } else if (greenConfirm === 0 && redConfirm === 0) {
        stage = CAPTAIN_WORKFLOW_STAGE.GREEN_ADJUSTMENT
        stepIndex = 1
    } else if (greenConfirm === 1 && redConfirm === 0) {
        stage = CAPTAIN_WORKFLOW_STAGE.RED_SELECTION
        stepIndex = 2
    } else if (
        (greenConfirm === 2 && (redConfirm === 0 || redConfirm === null))
        || (redConfirm === 2 && (greenConfirm === 0 || greenConfirm === null))
    ) {
        // 外战队伍已自动确认首发时，直接进入科朋队伍的阵容确认阶段。
        stage = CAPTAIN_WORKFLOW_STAGE.LINEUP_SETUP
        stepIndex = 3
    } else if (greenConfirm >= 1 && redConfirm >= 1) {
        stage = greenConfirm === 2 && redConfirm === 2
            ? CAPTAIN_WORKFLOW_STAGE.READY_TO_START
            : CAPTAIN_WORKFLOW_STAGE.LINEUP_SETUP
        stepIndex = 3
    }

    return {
        stage,
        stepIndex,
        steps,
        greenConfirm,
        redConfirm,
        showTeamSetup: stage !== CAPTAIN_WORKFLOW_STAGE.LINEUP_SETUP && stage !== CAPTAIN_WORKFLOW_STAGE.READY_TO_START,
        showLineupSetup: stage === CAPTAIN_WORKFLOW_STAGE.LINEUP_SETUP || stage === CAPTAIN_WORKFLOW_STAGE.READY_TO_START,
        // 首发已经确认但比赛尚未开始时仍允许整队对调，对调后双方重新排首发。
        canSwapCaptains: greenConfirm >= 1 && redConfirm >= 1,
        canStartMatch: stage === CAPTAIN_WORKFLOW_STAGE.READY_TO_START,
        bothCaptainsAssigned: greenConfirm !== null && redConfirm !== null
    }
}

// 保留现有业务权限：分队阶段只有管理员，以及主队调阵阶段的主队队长可直接修改成员。
export function canEditMemberAtCaptainStage(workflow, memberInfo, greenCaptainId) {
    // 命名阶段尚未进入调阵，管理员也只查看分队名单。
    if (workflow.stage === CAPTAIN_WORKFLOW_STAGE.TEAM_NAMING) return false
    if (memberInfo?.admin === 1) return true
    return workflow.stage === CAPTAIN_WORKFLOW_STAGE.GREEN_ADJUSTMENT
        && memberInfo?.id === greenCaptainId
}

// 队长和副队长都优先归属自己的队伍，避免兼任管理员时默认进入另一队。
export function getMemberLineupTeam({ gameDetail = {}, memberInfo, memberList = [] }) {
    const memberId = memberInfo?.id
    if (memberId == null) return null
    const isGreenLeader = String(memberId) === String(gameDetail.greenCaptainId) || memberList.some(item => (
        // 接口字段可能以数字或字符串返回，副队长始终按当前实际队伍取得排阵权限。
        String(item.id) === String(memberId) && Number(item.subCaptain) === 1 && Number(item.gameTeam) === 1
    ))
    const isRedLeader = String(memberId) === String(gameDetail.redCaptainId) || memberList.some(item => (
        String(item.id) === String(memberId) && Number(item.subCaptain) === 1 && Number(item.gameTeam) === 2
    ))

    // 保持旧页面的红队优先级，避免异常数据中同一成员同时属于两队时行为变化。
    return isRedLeader ? 2 : (isGreenLeader ? 1 : null)
}

// 仅管理员显式进入代排模式，或管理员本人不是本场队长时，才显示两队切换。
export function getLineupViewState({ gameDetail = {}, memberInfo, memberList = [], adminTeam = 1, adminOverride = false }) {
    const ownTeam = getMemberLineupTeam({ gameDetail, memberInfo, memberList })
    const editableTeam = memberInfo?.admin === 1 && Number(gameDetail.gameType) === 3
        && (adminOverride || !ownTeam)
        ? (adminTeam === 2 ? 2 : 1)
        : ownTeam
    if (!editableTeam) {
        return { mode: LINEUP_VIEW_MODE.READ_ONLY, editableTeam: null, substituteType: 'type3' }
    }

    const model = editableTeam === 1 ? gameDetail.greenModel : gameDetail.redModel
    const modelConfirm = editableTeam === 1 ? gameDetail.greenModelConfirm : gameDetail.redModelConfirm
    if (model == null) {
        return { mode: LINEUP_VIEW_MODE.CHOOSE_FORMATION, editableTeam, substituteType: 'type1' }
    }
    if (modelConfirm == null) {
        return {
            mode: LINEUP_VIEW_MODE.EDIT_LINEUP,
            editableTeam,
            substituteType: editableTeam === 1 ? 'type2' : 'type3'
        }
    }
    return { mode: LINEUP_VIEW_MODE.READ_ONLY, editableTeam, substituteType: 'type3' }
}
